import { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, ConfigProvider, Form, Input, Select } from 'antd';
import heIL from 'antd/locale/he_IL';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faMoneyBillWave, faSlidersH, faUsers } from '@fortawesome/pro-light-svg-icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  DEFAULT_BOX_SETTINGS,
  DEFAULT_STAFF_PROFILE,
  SalaryComponentsSchema,
  TAX_TYPE_LABELS,
  type InvoicePolicy,
  type StaffProfile,
  type StaffSalaryListItem,
} from '@salaries/shared';
import { useAuth } from '../../auth/AuthContext';
import { getBoxSalaryDefaults, updateBoxSalaryDefaults } from '../../api/salaryDefaults';
import { updateBoxSettings } from '../../api/boxSettings';
import { InvoicePolicyField } from '../settings/InvoicePolicyField';
import { listStaffSalarySettings } from '../../api/salarySettings';
import { refreshStaffMembers } from '../../api/staffMembers';
import { updateStaffProfile } from '../../api/staffProfile';
import { completeOnboarding } from '../../api/onboarding';
import { Loader } from '../../components/Loader';
import { StaffTaxBankFields } from '../settings/StaffTaxBankFields';
import {
  BaseFields,
  DEFAULT_COMPONENT_FLAT,
  TravelFields,
  WeekendFields,
  componentsToFlat,
  flatToComponents,
  type ComponentFlat,
} from '../settings/salaryComponentFields';

type StepKey = 'welcome' | 'defaults' | 'staff' | 'done';
const STEPS: { key: StepKey; label: string; icon: typeof faCheck }[] = [
  { key: 'welcome', label: 'ברוכים הבאים', icon: faCheck },
  { key: 'defaults', label: 'הגדרות שכר כלליות', icon: faMoneyBillWave },
  { key: 'staff', label: 'אנשי צוות', icon: faUsers },
  { key: 'done', label: 'סיום', icon: faSlidersH },
];

function StepHeader({ idx }: { idx: number }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {STEPS.map((s, i) => (
        <div key={s.key} className="flex items-center gap-2">
          {i > 0 && <div className={`w-8 h-px ${i <= idx ? 'bg-primary' : 'bg-border'}`} />}
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded text-small ${
              i === idx
                ? 'bg-primary text-white'
                : i < idx
                  ? 'text-primary'
                  : 'text-secondary-text'
            }`}
          >
            <FontAwesomeIcon icon={s.icon} style={{ fontSize: 12 }} />
            <span className="hidden sm:inline">{s.label}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// One staff row in the staff step: track toggle + tax type + expandable terms.
function StaffRow({
  item,
  profile,
  onProfile,
}: {
  item: StaffSalaryListItem;
  profile: StaffProfile;
  onProfile: (p: StaffProfile) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border border-border rounded mb-2">
      <div className="flex items-center gap-3 px-3 py-2">
        <Checkbox
          checked={profile.tracked}
          onChange={(e) => onProfile({ ...profile, tracked: e.target.checked })}
        />
        <span className="flex-1 text-main-text">{item.display_name}</span>
        {profile.tracked ? (
          <>
            <Select<StaffProfile['tax_type']>
              size="small"
              value={profile.tax_type}
              options={Object.entries(TAX_TYPE_LABELS).map(([v, l]) => ({ value: v as never, label: l }))}
              onChange={(v) => onProfile({ ...profile, tax_type: v })}
              style={{ width: 130 }}
            />
            <Button size="small" type="link" onClick={() => setExpanded((x) => !x)}>
              {expanded ? 'סגור' : 'הגדרות מותאמות'}
            </Button>
          </>
        ) : (
          <span className="text-small text-secondary-text">לא במעקב</span>
        )}
      </div>
      {profile.tracked && expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-border">
          <StaffTaxBankFields value={profile} staffName={item.display_name} onChange={(patch) => onProfile({ ...profile, ...patch })} />
        </div>
      )}
    </div>
  );
}

export function OnboardingWizard() {
  const auth = useAuth();
  const [stepIdx, setStepIdx] = useState(0);
  const step: StepKey = STEPS[stepIdx]!.key;
  const [saving, setSaving] = useState(false);

  // ── General defaults form ───────────────────────────────────────────────
  const [form] = Form.useForm<ComponentFlat>();
  const [invoicePolicy, setInvoicePolicy] = useState<InvoicePolicy>(
    auth.settings?.invoice_policy ?? 'optional',
  );
  const [accountantEmail, setAccountantEmail] = useState<string>(auth.settings?.accountant_email ?? '');
  const defaultsQuery = useQuery({ queryKey: ['salary-defaults'], queryFn: getBoxSalaryDefaults });
  useEffect(() => {
    if (defaultsQuery.data) form.setFieldsValue(componentsToFlat(defaultsQuery.data));
  }, [defaultsQuery.data, form]);

  // ── Staff list + per-staff profiles (local until finish) ────────────────
  const queryClient = useQueryClient();
  const staffQuery = useQuery({ queryKey: ['salary-settings'], queryFn: listStaffSalarySettings });
  // First-login bootstrap: the embed-login roster sync is fire-and-forget, so on a
  // brand-new box this list can read empty before the sync finishes. Force a sync
  // on mount and keep the loader up until it (and the refetch) complete — so the
  // wizard never flashes a false "no staff".
  const [bootstrapping, setBootstrapping] = useState(true);
  useEffect(() => {
    let alive = true;
    refreshStaffMembers()
      .catch(() => undefined)
      .then(() => queryClient.invalidateQueries({ queryKey: ['salary-settings'] }))
      .finally(() => {
        if (alive) setBootstrapping(false);
      });
    return () => {
      alive = false;
    };
  }, [queryClient]);
  const [profiles, setProfiles] = useState<Record<number, StaffProfile>>({});
  useEffect(() => {
    if (staffQuery.data) {
      setProfiles((prev) => {
        const next = { ...prev };
        for (const s of staffQuery.data) {
          if (!next[s.arbox_users_boxes_id]) {
            next[s.arbox_users_boxes_id] = {
              ...DEFAULT_STAFF_PROFILE,
              tracked: s.tracked,
              tax_type: s.tax_type,
            };
          }
        }
        return next;
      });
    }
  }, [staffQuery.data]);

  // Staff Arbox has deactivated are in the list (they can still be owed a past
  // period — see listStaffSalarySettings), but a first-time setup is about who
  // the box pays NOW: an ex-employee is noise here. They stay reachable later,
  // from the staff-settings page's "הצג אנשי צוות שאינם פעילים בארבוקס".
  const staff = (staffQuery.data ?? []).filter((s) => s.active || s.tracked);
  const trackedCount = useMemo(
    () => Object.values(profiles).filter((p) => p.tracked).length,
    [profiles],
  );

  const saveDefaults = async () => {
    const parsed = SalaryComponentsSchema.safeParse(flatToComponents(form.getFieldsValue(true)));
    if (parsed.success) await updateBoxSalaryDefaults(parsed.data);
    // Persist the box-level חשבונית policy alongside the salary defaults,
    // preserving any other box settings.
    await updateBoxSettings({
      ...(auth.settings ?? DEFAULT_BOX_SETTINGS),
      invoice_policy: invoicePolicy,
      accountant_email: accountantEmail.trim() || null,
    });
  };

  // Finish: persist defaults + any per-staff profile that changed, flip the flag.
  const finish = async () => {
    setSaving(true);
    try {
      await saveDefaults();
      // Save profiles that differ from the staff member's current server value.
      for (const s of staff) {
        const p = profiles[s.arbox_users_boxes_id];
        if (!p) continue;
        const changed =
          p.tracked !== s.tracked ||
          p.tax_type !== s.tax_type ||
          p.vat_mode !== 'excluded' ||
          p.withholding_pct > 0 ||
          p.business_id ||
          p.bank_name ||
          p.bank_branch ||
          p.bank_account ||
          p.bank_account_holder;
        if (changed) await updateStaffProfile(s.arbox_users_boxes_id, p);
      }
      await completeOnboarding();
      await auth.refetchMe();
      toast.success('ההגדרה הושלמה!');
    } catch (err) {
      toast.error((err as Error).message || 'שגיאה בשמירה');
      setSaving(false);
    }
  };

  const skipAll = async () => {
    setSaving(true);
    try {
      await completeOnboarding();
      await auth.refetchMe();
    } catch (err) {
      toast.error((err as Error).message || 'שגיאה');
      setSaving(false);
    }
  };

  const next = async () => {
    // Persist the defaults when leaving that step so progress isn't lost.
    if (step === 'defaults') await saveDefaults();
    setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  };
  const back = () => setStepIdx((i) => Math.max(i - 1, 0));

  return (
    <ConfigProvider locale={heIL} direction="rtl">
      <div className="min-h-screen flex items-start justify-center bg-grey-bg p-4 sm:p-8">
        <div className="w-full max-w-3xl bg-white rounded shadow-popup p-6 sm:p-8 mt-4 sm:mt-10">
          <StepHeader idx={stepIdx} />

          {step === 'welcome' && (
            <div className="text-center py-6">
              <h1 className="text-heading-1 text-main-text mb-3">ברוכים הבאים למערכת השכר 👋</h1>
              <p className="text-secondary-text mb-2">
                נגדיר יחד את ברירות המחדל לחישוב השכר ואת אנשי הצוות — כך שמסך השכר יהיה מוכן לעבודה.
              </p>
              <p className="text-small text-secondary-text">אפשר לדלג על כל שלב ולשנות הכל בהמשך מההגדרות.</p>
            </div>
          )}

          {step === 'defaults' && (
            <div>
              <h2 className="text-heading-3-bold text-main-text mb-1">הגדרות שכר כלליות</h2>
              <p className="text-small text-secondary-text mb-4">
                ברירת מחדל שתחול על כל אנשי הצוות. ניתן לדרוס לכל אדם בנפרד בשלב הבא או בהגדרות.
              </p>
              {defaultsQuery.isLoading ? (
                <div className="flex justify-center py-8"><Loader size={40} /></div>
              ) : (
                <Form form={form} layout="vertical" initialValues={DEFAULT_COMPONENT_FLAT} requiredMark={false}>
                  <div className="text-label font-medium text-main-text mb-2">שכר בסיס</div>
                  <p className="text-small text-secondary-text mb-2">
                    סכום חודשי קבוע (אופציונלי) — בנוסף לשכר השיעורים.
                  </p>
                  <BaseFields form={form} />

                  <div className="text-label font-medium text-main-text mb-2 mt-3">תוספת סוף שבוע</div>
                  <p className="text-small text-secondary-text mb-2">
                    אחוז תוספת על שכר השיעורים שמתקיימים בשישי ובשבת (ניתן להגדיר אחוז שונה לכל יום).
                  </p>
                  <WeekendFields form={form} />

                  <div className="text-label font-medium text-main-text mb-2 mt-3">נסיעות</div>
                  <p className="text-small text-secondary-text mb-2">
                    החזר נסיעות — סכום חודשי קבוע או סכום לכל יום עבודה בפועל.
                  </p>
                  <TravelFields form={form} />
                </Form>
              )}

              <div className="text-label font-medium text-main-text mb-1 mt-4">חשבוניות</div>
              <p className="text-small text-secondary-text mb-2">
                האם אנשי הצוות הפרילנסרים יצרפו חשבונית בעת אישור השכר.
              </p>
              <InvoicePolicyField value={invoicePolicy} onChange={setInvoicePolicy} />

              <div className="text-label font-medium text-main-text mb-1 mt-4">מייל לשליחת דוח שכר</div>
              <p className="text-small text-secondary-text mb-2">
                לכתובת זו יישלח דוח השכר המרוכז של תקופה סגורה (רו״ח / הנהלת חשבונות). ניתן להגדיר גם בהמשך.
              </p>
              <Input
                dir="ltr"
                inputMode="email"
                placeholder="accountant@example.com"
                value={accountantEmail}
                onChange={(e) => setAccountantEmail(e.target.value)}
              />
            </div>
          )}

          {step === 'staff' && (
            <div>
              <h2 className="text-heading-3-bold text-main-text mb-1">אנשי צוות</h2>
              <p className="text-small text-secondary-text mb-4">
                בחר אילו אנשי צוות לכלול במעקב השכר, סווג כל אחד (שכיר / עוסק פטור / עוסק מורשה),
                ואפשר להגדיר תנאים ופרטי בנק מותאמים. {trackedCount} במעקב.
              </p>
              {bootstrapping || staffQuery.isLoading ? (
                <div className="flex flex-col items-center gap-3 py-10">
                  <Loader size={40} />
                  <span className="text-small text-secondary-text">טוען אנשי צוות…</span>
                </div>
              ) : staff.length === 0 ? (
                <p className="text-secondary-text py-6">לא נמצאו אנשי צוות פעילים בעסק.</p>
              ) : (
                <div className="max-h-[55vh] overflow-auto pe-1">
                  {staff.map((s) => (
                    <StaffRow
                      key={s.arbox_users_boxes_id}
                      item={s}
                      profile={profiles[s.arbox_users_boxes_id] ?? DEFAULT_STAFF_PROFILE}
                      onProfile={(p) =>
                        setProfiles((prev) => ({ ...prev, [s.arbox_users_boxes_id]: p }))
                      }
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 'done' && (
            <div className="text-center py-6">
              <div className="text-success-green mb-3" style={{ fontSize: 42 }}>
                <FontAwesomeIcon icon={faCheck} />
              </div>
              <h2 className="text-heading-1 text-main-text mb-2">הכל מוכן!</h2>
              <p className="text-secondary-text">
                הגדרנו את ברירות המחדל ו-{trackedCount} אנשי צוות במעקב. אפשר תמיד לשנות מההגדרות.
              </p>
            </div>
          )}

          {/* Controls */}
          <div className="flex items-center justify-between mt-8 pt-4 border-t border-border">
            <div>
              {stepIdx > 0 && step !== 'done' && (
                <Button onClick={back} disabled={saving}>חזור</Button>
              )}
            </div>
            <div className="flex items-center gap-3">
              {step !== 'done' && (
                <button
                  type="button"
                  onClick={skipAll}
                  disabled={saving}
                  className="text-small text-secondary-text hover:text-main-text underline"
                >
                  דלג והגדר אחר כך
                </button>
              )}
              {step === 'done' ? (
                <Button type="primary" loading={saving} onClick={finish}>
                  סיום והתחלה
                </Button>
              ) : (
                <Button type="primary" onClick={next} disabled={saving}>
                  המשך
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
}
